import Quill from "quill";
import type * as ParchmentNS from "parchment";

const Parchment = Quill.import('parchment') as typeof ParchmentNS;

/**
 * A soft line break (Shift+Enter) — continues the CURRENT block (paragraph,
 * list item, …) on a new visual line instead of splitting into a new block.
 * Rendered as a plain `<br>`.
 *
 * Bugreport (Anke, 2026-08-31): inside a list, Shift+Enter behaved exactly
 * like Enter — both started a new list item, because Quill's default keyboard
 * module has no soft-break binding at all; its one generic `key: 'Enter',
 * shiftKey: null` handler (`handleEnter`) fires for BOTH and always inserts a
 * new BLOCK carrying the same `list` format, i.e. a new bullet/number every
 * time. Until the RTE unification (2026-09-14), a `linebreak` keyboard
 * binding in `ha-rte.component.ts` intercepted Shift+Enter specifically and
 * inserted THIS blot instead of a new block — that binding, and the rest of
 * `ha-rte`'s standalone Quill editor, is GONE now (replaced by
 * `@o2d/rich-text-editor`/TipTap, which handles Shift+Enter natively via its
 * own `hardBreak` node). This blot and its Delta shape (`{insert:
 * {lineBreak: true}}`) are only still relevant for RENDERING a Delta not yet
 * migrated to a `RichTextDocument` — `ha-quill-viewer` (the legacy-Delta
 * branch of the read-only viewer) is what still registers and reads it;
 * `QuillDeltaToRichTextDocumentConverter` converts it into a `hardBreak` node
 * the one time a matching field is next saved.
 *
 * Deliberately its OWN blot, not Quill's built-in `blots/break` (blotName
 * 'break', also a bare `<br>`): that one exists purely so an EMPTY line still
 * has something for the caret to sit on, and self-removes the instant it has
 * a text sibling (`optimize()`) — exactly the opposite of what a soft break
 * needs, since a soft break's entire point is to sit NEXT TO real text.
 *
 * `static value()` returns `true` (not `undefined`, unlike `blots/break`) so
 * this blot round-trips through the Delta as a real, JSON-serialisable
 * `{insert: {lineBreak: true}}` op — `undefined` would vanish on
 * `JSON.stringify` (this repo persists a not-yet-migrated field as a JSON
 * Delta), silently dropping every soft break the next time the issue loads.
 */
export class LineBreakBlot extends Parchment.EmbedBlot {

    static blotName = 'lineBreak';
    static tagName = 'BR';

    /**
     * Review-Lauf 02 (MAJOR, Linse B): without a `className`, Parchment's
     * `Registry.register()` unconditionally overwrites the GLOBAL
     * `tags['BR']` entry — `(this.tags[tag] == null || definition.className
     * == null) && (this.tags[tag] = definition)` in `parchment.js` — with
     * whichever blot registers itself for tag `BR` LAST. Quill's own
     * built-in `Break` blot (`quill/blots/break.js`, also `tagName: 'BR'`,
     * also no `className`) registers first, as a side effect of importing
     * `quill` itself (`core.js`'s module-level `Quill.register({'blots/
     * break': Break, ...})`); this blot's own registration (below) used to
     * run right after, silently replacing `tags['BR']` app-wide — for EVERY
     * Quill instance sharing the module-level `globalRegistry`
     * (`quill/core/quill.js`), not just this one. `Break` exists purely as a
     * self-removing empty-line placeholder (`optimize() { if (this.prev ||
     * this.next) this.remove(); }`) — this blot deliberately does NOT
     * self-remove (see the class doc above); any Quill-internal DOM
     * reconciliation path that classifies a raw `<br>` node via `tags['BR']`
     * (not via this blot's own `blotName`-keyed Delta ops, which are
     * unaffected either way) would silently get the WRONG blot.
     *
     * A `className` here is enough: Parchment's tag-registration check
     * short-circuits once `definition.className != null`, so `Break` keeps
     * sole ownership of the shared `tags['BR']` entry; this blot is still
     * fully reachable everywhere IT is actually used — by `blotName`
     * ('lineBreak'), which is a completely separate registry key untouched
     * by any of this. The class itself carries no CSS rule — purely to keep
     * the two blots from colliding in the tag registry.
     */
    static className = 'ql-line-break';

    static value(): true {
        return true;
    }

}

Quill.register(LineBreakBlot);
